import { BookmarkKind } from "@/bookmarks/__generated__/BookmarkButtonCreateMutation.graphql"
import ContentAttachmentsSection, {
  ContentAttachmentsSectionSkeleton,
} from "@/content/detail/sections/ContentAttachmentsSection"
import ContentDescriptionSection, {
  ContentDescriptionSectionSkeleton,
} from "@/content/detail/sections/ContentDescriptionSection"
import ContentFooterSection, {
  ContentFooterSectionSkeleton,
} from "@/content/detail/sections/ContentFooterSection"
import ContentHeaderSection, {
  ContentHeaderSectionSkeleton,
} from "@/content/detail/sections/ContentHeaderSection"
import ContentMediaSection, {
  ContentMediaSectionSkeleton,
} from "@/content/detail/sections/ContentMediaSection"
import ContentTitleSection, {
  ContentTitleSectionSkeleton,
} from "@/content/detail/sections/ContentTitleSection"
import { ContentCard_ContentFragment$key } from "@/content/detail/__generated__/ContentCard_ContentFragment.graphql"
import { ContentCard_ContentUsageFragment$key } from "@/content/detail/__generated__/ContentCard_ContentUsageFragment.graphql"
import makeUseStyles from "@/core/ui/style/util/makeUseStyles"
import Relay from "@/relay/relayUtils"
import defaultUserCoverPhoto from "@assets/images/covers/default-product-card-cover.png"
import styleIf from "@assets/style/util/styleIf"
import useShowOnHoverStyles from "@assets/style/util/useShowOnHoverStyles"
import CoverPhoto from "@components/cover-photo/CoverPhoto"
import DiscoContainerButton from "@disco-ui/button/DiscoContainerButton"
import DiscoVideo from "@disco-ui/video/DiscoVideo"
import { Theme, useMediaQuery } from "@material-ui/core"
import classNames from "classnames"
import { useFragment } from "react-relay"
import { useHistory } from "react-router-dom"
import { graphql } from "relay-runtime"

export type ContentCardCoverPosition = "top" | "under_header" | "left"

export interface ContentCardProps {
  contentKey: ContentCard_ContentFragment$key
  contentUsageKey?: ContentCard_ContentUsageFragment$key
  testid: string
  onClick?: VoidFunction
  onCommentButtonClick?: VoidFunction
  detailPagePath?: string
  banner?: React.ReactNode
  overflow?: React.ReactNode
  headerContent?: React.ReactNode
  titleContent?: React.ReactNode
  headerRightContent?: React.ReactNode
  footerRightContent?: React.ReactNode
  className?: string
  hideHeader?: boolean
  hideDescription?: boolean
  hideTitle?: boolean
  hideMedia?: boolean
  hideAttachments?: boolean
  compact?: boolean
  hasMedia?: boolean
  readOnlyReactionButton?: boolean
  bookmarkKind?: BookmarkKind
  classes?: {
    overflow?: string
    footerContainer?: string
  }
  coverPosition?: ContentCardCoverPosition
}

function ContentCard({
  contentKey,
  contentUsageKey,
  testid,
  onClick,
  onCommentButtonClick,
  detailPagePath,
  banner,
  overflow,
  headerContent,
  titleContent,
  headerRightContent,
  footerRightContent,
  className,
  hideHeader = false,
  hideDescription = false,
  hideMedia = false,
  hideAttachments = false,
  hasMedia = true,
  hideTitle = false,
  compact = false,
  readOnlyReactionButton,
  bookmarkKind,
  coverPosition = "under_header",
  classes: propsClasses,
}: ContentCardProps) {
  const history = useHistory()

  const content = useFragment<ContentCard_ContentFragment$key>(
    graphql`
      fragment ContentCard_ContentFragment on Content {
        id
        coverVideo
        coverVideoAssetUsage {
          id
          asset {
            id
            ...DiscoVideoFragment
          }
        }
        coverPhoto
        thumbnailUrl
        showCoverPlayIcon
        ...ContentHeaderSectionFragment
        ...ContentMediaSectionFragment
        ...ContentTitleSectionFragment
        ...ContentDescriptionSectionFragment
        ...ContentAttachmentsSectionFragment
        ...ContentFooterSection_ContentFragment
        ...BookmarkButtonContentFragment
      }
    `,
    contentKey
  )

  const contentUsage = useFragment<ContentCard_ContentUsageFragment$key>(
    graphql`
      fragment ContentCard_ContentUsageFragment on ContentUsage {
        id
        ...ContentFooterSection_ContentUsageFragment
      }
    `,
    contentUsageKey || null
  )

  const isMdScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))
  const classes = useStyles({ coverPosition, hasMedia })
  const onShowHoverClasses = useShowOnHoverStyles()

  if (!content && !contentUsage) return null

  if (coverPosition === "left") {
    return (
      <DiscoContainerButton
        className={classNames(classes.container, className, onShowHoverClasses.hoverable)}
        testid={testid}
        onClick={handleGoToDetailPage}
      >
        {!hideMedia && (
          <ContentMediaSection
            className={classes.media}
            contentKey={content}
            contentUsageId={contentUsage?.id}
            testid={testid}
            objectFit
          />
        )}
        <div className={classes.body}>
          {!hideTitle &&
            (titleContent || (
              <ContentTitleSection
                className={classes.title}
                contentKey={content}
                testid={testid}
                textVariant={"body-md-600"}
              />
            ))}

          {!hasMedia && (
            <ContentDescriptionSection
              className={classes.description}
              contentKey={content}
              contentUsageId={contentUsage?.id}
              testid={testid}
              disableMediaModal
            />
          )}

          <div
            className={classNames(classes.footerContainer, propsClasses?.footerContainer)}
          >
            <ContentFooterSection
              contentUsageKey={contentUsage!}
              contentKey={content}
              testid={testid}
              onCommentButtonClick={handleGoToDetailPage}
              hideText={compact || isMdScreen}
              readOnlyReactionButton={readOnlyReactionButton}
              bookmarkKind={bookmarkKind}
              customHoverClasses={onShowHoverClasses.showable}
              hideReactionsList={compact}
              hideBookmarkText
              footerRightContent={footerRightContent}
            />
          </div>
        </div>
      </DiscoContainerButton>
    )
  }

  return (
    <DiscoContainerButton
      className={classNames(classes.container, onShowHoverClasses.hoverable, className)}
      testid={testid}
      onClick={handleGoToDetailPage}
    >
      {!hideMedia &&
        coverPosition === "top" &&
        (content.coverVideo || content.coverVideoAssetUsage ? (
          <DiscoVideo
            radius={"big"}
            src={content.coverVideo}
            // thumbnailUrl was created to replace coverPhoto, but both are still used so fallback to coverPhoto
            poster={content.thumbnailUrl || content.coverPhoto}
            assetKey={content.coverVideoAssetUsage?.asset}
            className={classes.videoWrapper}
            disablePlay
            playButton={{ hidden: !content.showCoverPlayIcon, size: "small" }}
            isGridItem
            aspectRatio={"2"}
          />
        ) : (
          <CoverPhoto
            coverPhoto={
              content.thumbnailUrl || content.coverPhoto || defaultUserCoverPhoto
            }
            customClassName={classes.coverPhoto}
            isGridItem
          />
        ))}

      <div className={classes.body}>
        {hideHeader
          ? null
          : headerContent || (
              <ContentHeaderSection
                classes={{ overflow: propsClasses?.overflow }}
                className={classes.header}
                contentKey={content}
                testid={testid}
                overflow={
                  <>
                    <>{overflow}</>
                    <>{headerRightContent}</>
                  </>
                }
              />
            )}

        {banner}

        {!hideMedia && coverPosition === "under_header" && (
          <ContentMediaSection
            className={classes.media}
            contentKey={content}
            contentUsageId={contentUsage?.id}
            testid={testid}
          />
        )}

        <div className={classes.textContainer}>
          {!hideTitle &&
            (titleContent || (
              <ContentTitleSection
                className={classes.title}
                contentKey={content}
                testid={testid}
              />
            ))}

          {!hideDescription && (
            <ContentDescriptionSection
              className={classes.description}
              contentKey={content}
              contentUsageId={contentUsage?.id}
              testid={testid}
              disableMediaModal
            />
          )}
        </div>

        {!hideAttachments && (
          <ContentAttachmentsSection
            className={classes.attachments}
            contentKey={content}
            testid={testid}
          />
        )}

        <div
          className={classNames(classes.footerContainer, propsClasses?.footerContainer)}
        >
          <ContentFooterSection
            {...(contentUsage
              ? { contentUsageKey: contentUsage! }
              : { contentKey: content })}
            testid={testid}
            onCommentButtonClick={onCommentButtonClick}
            hideText={compact || isMdScreen}
            readOnlyReactionButton={readOnlyReactionButton}
            bookmarkKind={bookmarkKind}
            customHoverClasses={onShowHoverClasses.showable}
            hideReactionsList={compact}
            hideBookmarkText
            footerRightContent={footerRightContent}
          />
        </div>
      </div>
    </DiscoContainerButton>
  )

  function handleGoToDetailPage() {
    if (detailPagePath) {
      history.push(detailPagePath)
    } else if (onClick) {
      onClick()
    }
  }
}

interface StyleProps {
  coverPosition: ContentCardCoverPosition
  hasMedia: boolean
}

const useStyles = makeUseStyles((theme) => ({
  container: ({ coverPosition }: StyleProps) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.measure.borderRadius.big,
    overflow: "hidden",
    border: theme.palette.constants.borderDashboardCard,
    display: "flex",
    height: "100%",
    ...styleIf(coverPosition === "under_header", {
      flexDirection: "column",
    }),
    ...styleIf(coverPosition === "left" || coverPosition === "top", {
      padding: 0,
    }),
  }),

  header: ({ coverPosition }: StyleProps) => ({
    marginBottom: theme.spacing(2.5),

    ...styleIf(coverPosition === "left", {
      justifyContent: "space-between",
    }),
  }),
  media: ({ coverPosition }: StyleProps) => ({
    padding: 0,
    ...styleIf(coverPosition === "left", {
      height: "130px",
      minWidth: "260px",
    }),
    ...styleIf(coverPosition === "under_header", {
      marginBottom: theme.spacing(2.5),
    }),
    ...styleIf(coverPosition === "top", {
      borderTopLeftRadius: theme.measure.borderRadius.big,
      borderTopRightRadius: theme.measure.borderRadius.big,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      height: "100%",
    }),
  }),
  // This ensures that the text takes up the remaining space and pushes the footer to the bottom of the card
  textContainer: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
  },
  title: { marginBottom: theme.spacing(1) },
  description: { marginBottom: theme.spacing(2.5) },
  attachments: { marginBottom: theme.spacing(2.5) },
  footerContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  body: ({ coverPosition, hasMedia }: StyleProps) => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "100%",

    ...styleIf(!hasMedia, {
      gap: theme.spacing(1.5),
    }),

    ...styleIf(coverPosition === "left" || coverPosition === "top", {
      padding: theme.spacing(2),
    }),
  }),

  videoWrapper: {
    // To make the video container the same size as the cover photo
    aspectRatio: "2",
    borderRadius: 0,
    borderTopRightRadius: theme.measure.borderRadius.big,
    borderTopLeftRadius: theme.measure.borderRadius.big,
    overflow: "clip",
  },
  coverPhoto: {
    width: "100%",
    objectFit: "cover",
    borderRadius: 0,
    borderTopRightRadius: theme.measure.borderRadius.big,
    borderTopLeftRadius: theme.measure.borderRadius.big,
  },
}))

export function ContentCardSkeleton() {
  const classes = useStyles({ coverPosition: "under_header", hasMedia: false })
  return (
    <div className={classes.container}>
      <ContentHeaderSectionSkeleton className={classes.header} />
      <ContentMediaSectionSkeleton className={classes.media} />
      <ContentTitleSectionSkeleton className={classes.title} />
      <ContentDescriptionSectionSkeleton className={classes.description} />
      <ContentAttachmentsSectionSkeleton />
      <div className={classes.footerContainer}>
        <ContentFooterSectionSkeleton />
      </div>
    </div>
  )
}

export default Relay.withSkeleton({
  component: ContentCard,
  skeleton: ContentCardSkeleton,
})
